import * as React from 'react'
import { User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { userMenuItems } from '../../config/navbar-data'
import { useUserStore } from '../../stores/user-store'
import { filterUserMenuItems } from '../../lib/navigation-utils'

interface NavbarUserMenuProps {
  align?: 'start' | 'center' | 'end'
}

export default function NavbarUserMenu({ align = 'end' }: NavbarUserMenuProps) {
  const { user, tenant } = useUserStore()
  const filteredItems = filterUserMenuItems(userMenuItems, user, tenant)
  const isImpersonating = user?.impersonating === true
  
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={isImpersonating ? "default" : "ghost"}
          size="icon" 
          className={`relative ${isImpersonating ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : ''}`}
        >
          <User className="!h-5 !w-5" />
          {isImpersonating && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-orange-500 ring-2 ring-background" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align={align}>
        <DropdownMenuLabel>
          <div>
            <div>
              Signed in as:
              {isImpersonating && (
                <span className="ml-2 text-xs text-orange-600 font-medium">(Masquerading)</span>
              )}
            </div>
            <div className="text-muted-foreground text-sm font-normal">{user?.name || 'Guest'}</div>
            {user && (
              <div className="text-muted-foreground text-xs font-normal">
                Role:
                {' '}
                {
                  user.superadmin
                    ? 'Super Admin'
                    : user.tenant_admin
                      ? 'Tenant Admin'
                      : user.admin
                        ? 'Admin'
                        : user.rep
                          ? 'Advisor'
                          : user.member
                            ? 'Member'
                            : 'User'
                }
              </div>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {filteredItems.map((item, index) => {
          const isStopMasquerading = item.label === 'Stop Masquerading'
          return (
            <React.Fragment key={`${item.href}-${index}`}>
              {item.separator === 'before' && <DropdownMenuSeparator />}
              <DropdownMenuItem 
                asChild
                className={isStopMasquerading ? '!bg-primary hover:!bg-primary/90' : ''}
              >
                <a
                  href={item.href}
                  data-method={item.dataMethod}
                  className={isStopMasquerading 
                    ? '!text-white hover:!text-white' 
                    : item.className}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                >
                  {item.label}
                </a>
              </DropdownMenuItem>
              {item.separator === 'after' && <DropdownMenuSeparator />}
            </React.Fragment>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
